import { __extends } from "tslib";
import { WebXRFeaturesManager, WebXRFeatureName } from "../webXRFeaturesManager.js";
import { Observable } from "../../Misc/observable.js";
import { Vector3, Matrix } from "../../Maths/math.vector.js";
import { WebXRAbstractFeature } from "./WebXRAbstractFeature.js";
import { Tools } from "../../Misc/tools.js";
/**
 * The currently-working hit-test module.
 * Hit test (or Ray-casting) is used to interact with the real world.
 * For further information read here - https://github.com/immersive-web/hit-test
 */
var WebXRHitTestLegacy = /** @class */ (function (_super) {
    __extends(WebXRHitTestLegacy, _super);
    /**
     * Creates a new instance of the (legacy version) hit test feature
     * @param _xrSessionManager an instance of WebXRSessionManager
     * @param options options to use when constructing this feature
     */
    function WebXRHitTestLegacy(_xrSessionManager, 
    /**
     * options to use when constructing this feature
     */
    options) {
        if (options === void 0) { options = {}; }
        var _this = _super.call(this, _xrSessionManager) || this;
        _this.options = options;
        // in XR space z-forward is negative
        _this._direction = new Vector3(0, 0, -1);
        _this._mat = new Matrix();
        _this._onSelectEnabled = false;
        _this._origin = new Vector3(0, 0, 0);
        /**
         * Populated with the last native XR Hit Results
         */
        _this.lastNativeXRHitResults = [];
        /**
         * Triggered when new babylon (transformed) hit test results are available
         */
        _this.onHitTestResultObservable = new Observable();
        _this._onHitTestResults = function (xrResults) {
            var mats = xrResults.map(function (result) {
                var mat = Matrix.FromArray(result.hitMatrix);
                if (!_this._xrSessionManager.scene.useRightHandedSystem) {
                    mat.toggleModelMatrixHandInPlace();
                }
                // if (this.options.coordinatesSpace === Space.WORLD) {
                if (_this.options.worldParentNode) {
                    mat.multiplyToRef(_this.options.worldParentNode.getWorldMatrix(), mat);
                }
                return {
                    xrHitResult: result,
                    transformationMatrix: mat,
                };
            });
            _this.lastNativeXRHitResults = xrResults;
            _this.onHitTestResultObservable.notifyObservers(mats);
        };
        // can be done using pointerdown event, and xrSessionManager.currentFrame
        _this._onSelect = function (event) {
            if (!_this._onSelectEnabled) {
                return;
            }
            WebXRHitTestLegacy.XRHitTestWithSelectEvent(event, _this._xrSessionManager.referenceSpace);
        };
        _this.xrNativeFeatureName = "hit-test";
        Tools.Warn("A newer version of this plugin is available");
        return _this;
    }
    /**
     * execute a hit test with an XR Ray
     *
     * @param xrSession a native xrSession that will execute this hit test
     * @param xrRay the ray (position and direction) to use for ray-casting
     * @param referenceSpace native XR reference space to use for the hit-test
     * @param filter filter function that will filter the results
     * @returns a promise that resolves with an array of native XR hit result in xr coordinates system
     */
    WebXRHitTestLegacy.XRHitTestWithRay = function (xrSession, xrRay, referenceSpace, filter) {
        return xrSession.requestHitTest(xrRay, referenceSpace).then(function (results) {
            var filterFunction = filter || (function (result) { return !!result.hitMatrix; });
            return results.filter(filterFunction);
        });
    };
    /**
     * Execute a hit test on the current running session using a select event returned from a transient input (such as touch)
     * @param event the (select) event to use to select with
     * @param referenceSpace the reference space to use for this hit test
     * @returns a promise that resolves with an array of native XR hit result in xr coordinates system
     */
    WebXRHitTestLegacy.XRHitTestWithSelectEvent = function (event, referenceSpace) {
        var targetRayPose = event.frame.getPose(event.inputSource.targetRaySpace, referenceSpace);
        if (!targetRayPose) {
            return Promise.resolve([]);
        }
        var targetRay = new XRRay(targetRayPose.transform);
        return this.XRHitTestWithRay(event.frame.session, targetRay, referenceSpace);
    };
    /**
     * attach this feature
     * Will usually be called by the features manager
     *
     * @returns true if successful.
     */
    WebXRHitTestLegacy.prototype.attach = function () {
        if (!_super.prototype.attach.call(this)) {
            return false;
        }
        if (this.options.testOnPointerDownOnly) {
            this._xrSessionManager.session.addEventListener("select", this._onSelect, false);
        }
        return true;
    };
    /**
     * detach this feature.
     * Will usually be called by the features manager
     *
     * @returns true if successful.
     */
    WebXRHitTestLegacy.prototype.detach = function () {
        if (!_super.prototype.detach.call(this)) {
            return false;
        }
        // disable select
        this._onSelectEnabled = false;
        this._xrSessionManager.session.removeEventListener("select", this._onSelect);
        return true;
    };
    /**
     * Dispose this feature and all of the resources attached
     */
    WebXRHitTestLegacy.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this.onHitTestResultObservable.clear();
    };
    WebXRHitTestLegacy.prototype._onXRFrame = function (frame) {
        // make sure we do nothing if (async) not attached
        if (!this.attached || this.options.testOnPointerDownOnly) {
            return;
        }
        var pose = frame.getViewerPose(this._xrSessionManager.referenceSpace);
        if (!pose) {
            return;
        }
        Matrix.FromArrayToRef(pose.transform.matrix, 0, this._mat);
        Vector3.TransformCoordinatesFromFloatsToRef(0, 0, 0, this._mat, this._origin);
        Vector3.TransformCoordinatesFromFloatsToRef(0, 0, -1, this._mat, this._direction);
        this._direction.subtractInPlace(this._origin);
        this._direction.normalize();
        var ray = new XRRay({ x: this._origin.x, y: this._origin.y, z: this._origin.z, w: 0 }, { x: this._direction.x, y: this._direction.y, z: this._direction.z, w: 0 });
        WebXRHitTestLegacy.XRHitTestWithRay(this._xrSessionManager.session, ray, this._xrSessionManager.referenceSpace).then(this._onHitTestResults);
    };
    /**
     * The module's name
     */
    WebXRHitTestLegacy.Name = WebXRFeatureName.HIT_TEST;
    /**
     * The (Babylon) version of this module.
     * This is an integer representing the implementation version.
     * This number does not correspond to the WebXR specs version
     */
    WebXRHitTestLegacy.Version = 1;
    return WebXRHitTestLegacy;
}(WebXRAbstractFeature));
export { WebXRHitTestLegacy };
//register the plugin versions
WebXRFeaturesManager.AddWebXRFeature(WebXRHitTestLegacy.Name, function (xrSessionManager, options) {
    return function () { return new WebXRHitTestLegacy(xrSessionManager, options); };
}, WebXRHitTestLegacy.Version, false);
//# sourceMappingURL=WebXRHitTestLegacy.js.map